import {SpavaObj} from './SpavaObj'
import uniqBy from 'lodash/uniqBy'

class Institution extends SpavaObj {
  name = ''
  description = ''
  code = ''

  constructor (o) {
    super(o)
    if (typeof o === 'string') {
      this.name = o
    }
    else if (o instanceof Object) {
      let {name, description, code} = o
      this.name = name || ''
      this.code = code || ''
      this.description = description || ''
    }
  }

  static fromAPI (o) {
    return new Institution(o)
  }
}

class GroupType extends SpavaObj {
  name = ''
  description = ''

  constructor (o) {
    super(o)
    if (typeof o === 'string') {
      this.name = o
    }
    else if (o instanceof Object) {
      let {name, description} = o
      this.name = name || ''
      this.description = description || ''
    }
  }

  static fromAPI (o) {
    return new GroupType(o)
  }
}

class Group extends SpavaObj {
  static ORGANIZATION = 'org'
  static VIOLATION = 'vio'
  static PASS = 'pas'

  name = ''
  description = ''
  code = ''
  level = 1
  typeList = []
  roleList = []
  institution = null
  parent = null
  osdMsgIn=''
  osdMsgOut=''
  vcbMsgIn=''
  vcbMsgOut=''
  extend = ''
  constructor (o) {
    if (o instanceof Group) return o
    super(o)
    if (typeof o === 'string') {
      this.name = o
      return
    }
    o = o || {}

    let {name, description, code, level, typeList, roleList, osdMsgIn, osdMsgOut, vcbMsgIn, vcbMsgOut} = o
    this.name = name || ''
    this.code = code || ''
    this.level = level || ''
    this.description = description || ''
    this.osdMsgIn = osdMsgIn || ''
    this.osdMsgOut = osdMsgOut || ''
    this.vcbMsgIn = vcbMsgIn || ''
    this.vcbMsgOut = vcbMsgOut || ''
    this.typeList = typeList instanceof Array
      ? typeList.map(t => t instanceof GroupType ? t : new GroupType(t))
      : []
    this.roleList = roleList instanceof Array
      ? roleList.map(r => r instanceof Role ? r : new Role(r))
      : []
    this.institution = new Institution(o.institution)
  }

  addGroupType (...args) {
    let ps = args.map(p => p instanceof GroupType ? p : new GroupType(p))
    this.typeList = uniqBy([...this.typeList, ...ps], 'id')
  }

  deleteGroupType (...args) {
    this.typeList = this.typeList.filter(t => args.every(a => a.id !== t.id))
  }

  addRole (...args) {
    let ps = args.map(p => p instanceof Role ? p : new Role(p))
    this.roleList = uniqBy([...this.roleList, ...ps], 'id')
  }

  deleteRole (...args) {
    this.roleList = this.roleList.filter(t => args.every(a => a.id !== t.id))
  }

  isTypeOf (name) {
    return !!this.typeList.find(t => t.name === name)
  }

  get insName () {
    return this.institution && this.institution.name
  }

  get insId () {
    return this.institution && this.institution.id
  }

  get parentName () {
    return this.parent ? this.parent.name : this.insName
  }

  get parentId () {
    return this.parent ? this.parent.id : this.insId
  }

  get type () {
    return this.typeList.map(t => t.description).join(', ')
  }

  getIns () {
    return this.institution
  }

  static fromAPI (o = {}) {
    let g = new Group(o)
    g.level = o.codeLevel || 1
    if (o.groupTypes instanceof Array) {
      g.typeList = o.groupTypes.map(t => GroupType.fromAPI(t))
    }
    if (o.institution instanceof Array && o.institution.length) {
      g.institution = new Institution(o.institution[0])
    }
    if (o.extend !== null) {
      let extend = JSON.parse(o.extend)
      g.osdMsgIn = extend.osdMsgIn
      g.osdMsgOut = extend.osdMsgOut
      g.vcbMsgIn = extend.vcbMsgIn
      g.vcbMsgOut = extend.vcbMsgOut
    }
    return g
  }
}

class Permission extends SpavaObj {
  static MENU = 'menu'
  static BUTTON = 'button'
  static ELEMENT = 'element'
  static FILE = 'file'
  static OPERATION = 'operation'
  static UNKNOWN = ''
  static TYPE_LIST = [
    Permission.MENU,
    Permission.BUTTON,
    Permission.ELEMENT,
    Permission.FILE,
    Permission.OPERATION,
  ]
  name = ''
  description = ''
  shiroStr = ''
  type = null

  constructor (o) {
    if (o instanceof Permission) return o
    super(o)
    o = o || {}
    this.name = o.name || ''
    this.description = o.description || ''
    this.shiroStr = o.shiroStr || ''
    this.type = o.type || Permission.UNKNOWN
  }

  static fromAPI (o) {
    let p = new Permission(o)
    p.type = o.resourceType || Permission.UNKNOWN
    return p
  }
}

class Role extends SpavaObj {
  name = ''
  shiroStr = ''
  description = ''
  permissionList = []

  constructor (o) {
    if (o instanceof Role) return o
    super(o)
    o = o || {}
    this.name = o.name || ''
    this.description = o.description || ''
    this.shiroStr = o.shiroStr || ''
  }

  static fromAPI (o) {
    let r = new Role(o)
    if (o.permissions instanceof Array) {
      r.permissionList = o.permissions.map(p => Permission.fromAPI(p))
    }
    return r
  }

  addPermission (...args) {
    let ps = args.map(p => p instanceof Permission ? p : new Permission(p))
    // this.permissionList.push(...ps)
    this.permissionList = uniqBy([...this.permissionList, ...ps], 'id')
  }

  deletePermission (...args) {
    this.permissionList = this.permissionList.filter(p => args.every(a => a.id !== p.id))
  }

  is (r) {
    if (typeof r === 'string') {
      return this.shiroStr === r
    }
  }
}

class Person extends SpavaObj {
  static REGISTER = 0
  static VISITOR = 1
  static PASS_GROUP = 2
  static UNKNOWN = -1

  constructor (o = {}) {
    if (o instanceof Person) return o
    super(o)
    // console.log('set Person', o)
    let {name, loginId, phone, email, institution, roleList, groupList, type, typeName, idNumber, idType, extend, driverOrgs, driverLicense,openIds,orgName} = o
    this.name = name || ''
    this.orgName = orgName || ''
    this.loginId = loginId || ''
    this.phone = phone || ''
    this.email = email || ''
    this.type = type || Person.UNKNOWN
    this.typeName = typeName || ''
    this.idNumber = idNumber || ''
    this.idType = idType || ''
    this.institution = null
    this.groupList = []
    this.roleList = []
    this.openIds = openIds||''
    this.extend = extend || []
    this.driverOrgs = driverOrgs || []
    this.driverLicense = driverLicense || []
    this.regCode = NaN

    if (groupList instanceof Array) {
      this.groupList.push(...groupList.filter(g => g instanceof Group))
    }

    if (roleList instanceof Array) {
      this.roleList.push(...roleList.filter(r => r instanceof Role))
    }

    if (institution instanceof Institution) {
      this.institution = new Institution(institution)
    }

    // this.reservations = reservations || []
    // this.passLogs = passLogs || []
    // this.violations = violations || []
  }

  static fromAPI (o) {
    o.phone = o.mobilePhone
    o.typeName = o.authType
    let p = new Person(o)
    let groups = o.userGroups instanceof Array ? o.userGroups : []
    p.groupList = groups.map(g => Group.fromAPI(g))
    // console.log('person groups', o, p.groupList)
    if (p.groupList.length > 0 && !p.institution) {
      p.institution = p.groupList[0].getIns()
    }
    if (o.regCode !== null) {
      p.regCode = o.regCode
    }

    if (o.roleList instanceof Array) {
      p.addRole(...o.roleList.map(r => Role.fromAPI(r)))
    }
    if (o.extend !== null) {
      let extend = JSON.parse(o.extend)
      let array = []
      for (let v in extend) {
        if (v === 'driverOrgs') {
          p.driverOrgs = extend[v]
          continue
        }
        if (v === 'file') {
          let file = {}
          try {
            file = JSON.parse(extend[v])
          }
          catch (e) {
            continue
          }
          if (file.pic === undefined) {
            continue
          }
          if (file.pic.length === 0) {
            continue
          }
          if (file.pic[0].url1 === '') {
            continue
          }
          let img = new Image()
          img.src = 'https://smaco2.uestc.edu.cn/passApi' + file.pic[0].url1
          p.driverLicense.push(img)
          continue
        }
        let obj = {}
        obj.key = v
        obj.value = extend[v]
        array.push(obj)
      }
      p.extend = array
    }
    return p
  }

  // get isVisitor () {
  //   return this.type === Person.VISITOR
  // }
  //
  // get isRegister () {
  //   return this.type === Person.REGISTER
  // }
  //
  // get isPassGroup () {
  //   return this.type === Person.PASS_GROUP
  // }

  get insName () {
    return this.institution && this.institution.name
  }

  get insId () {
    return this.institution && this.institution.id
  }

  getIns () {
    return this.institution
  }

  get orgGroup () {
    return this.groupList.find(g => g.isTypeOf('org'))
  }

  get orgGroupName () {
    return this.orgGroup ? this.orgGroup.name : ''
  }
  get orgId () {
    return this.orgGroup ? this.orgGroup.id : ''
  }

  isRoleOf (s) {
    if (typeof s === 'string') {
      return this.roleList.some(r => r.is(s))
    }
    if (s instanceof Array) {
      return s.map(roleStr => this.roleList.some(role => role.is(roleStr)))
    }
  }

  addGroup (...args) {
    let ps = args.map(p => p instanceof Group ? p : new Group(p))
    // this.permissionList.push(...ps)
    this.groupList = uniqBy([...this.groupList, ...ps], 'id')
  }

  deleteGroup (...args) {
    this.groupList = this.groupList.filter(p => args.every(a => a.id !== p.id))
  }

  addRole (...args) {
    let ps = args.map(p => p instanceof Role ? p : new Role(p))
    // this.permissionList.push(...ps)
    this.roleList = uniqBy([...this.roleList, ...ps], 'id')
  }

  deleteRole (...args) {
    this.roleList = this.roleList.filter(p => args.every(a => a.id !== p.id))
  }

  setOrgGroup (group) {
    if (group instanceof Group) {
      let i = this.groupList.findIndex(g => g.id === group.id)
      if (i > -1) {
        let t = this.groupList[i]
        this.groupList.splice(i, 1)
        this.groupList.unshift(t)
      }
    }
  }
}

export {
  Group,
  Institution,
  Person,
  GroupType,
  Permission,
  Role,
}
